import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Search as SearchIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import ProveedorSearch from '../../components/ProveedorSearch';
import UbicacionSearch from '../../components/UbicacionSearch';
import productoService from '../../services/productoService';
import remitoCompraService from '../../services/remitoCompraService';
import { formatCurrency } from '../../utils/formatters';
import { getApiErrorMessage } from '../../utils/apiError';

interface RemitoCompraItemForm {
  codigoArticulo: string;
  descripcion: string;
  cantidad: number;
  precioUnitario: number;
  porcDesc: number;
  notas: string;
}

interface RemitoCompraFormularioForm {
  nroSuc: number;
  nroProveedor: number;
  nombre?: string;
  cuit?: string;
  descripcion?: string;

  fechaEmision: string;
  fechaVencim?: string;
  nroLista?: string;

  nroUbicacionOrigen?: number;
  direccionEnvio?: string;
  codigoCiudadEnvio?: number;
  formaEntrega?: string;

  porcDesc?: number;
  porcDesc2?: number;
  porcDesc3?: number;
}

const defaultItem: RemitoCompraItemForm = {
  codigoArticulo: '',
  descripcion: '',
  cantidad: 1,
  precioUnitario: 0,
  porcDesc: 0,
  notas: '',
};

const todayIsoDate = (): string => new Date().toISOString().split('T')[0];

const RemitoCompraForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [formulario, setFormulario] = useState<RemitoCompraFormularioForm>({
    nroSuc: 1,
    nroProveedor: 0,
    nombre: '',
    cuit: '',
    descripcion: '',
    fechaEmision: todayIsoDate(),
    fechaVencim: todayIsoDate(),
    nroLista: '',
    nroUbicacionOrigen: undefined,
    direccionEnvio: '',
    codigoCiudadEnvio: undefined,
    formaEntrega: '',
    porcDesc: undefined,
    porcDesc2: undefined,
    porcDesc3: undefined,
  });

  const [proveedorNombre, setProveedorNombre] = useState('');
  const [ubicacionNombre, setUbicacionNombre] = useState('');

  const [openProveedorSearch, setOpenProveedorSearch] = useState(false);
  const [openUbicacionSearch, setOpenUbicacionSearch] = useState(false);

  const [items, setItems] = useState<RemitoCompraItemForm[]>([{ ...defaultItem }]);
  const [saving, setSaving] = useState(false);

  // Producto search modal
  const [openProductSearch, setOpenProductSearch] = useState(false);
  const [productSearchIndex, setProductSearchIndex] = useState<number | null>(null);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productos, setProductos] = useState<any[]>([]);
  const [productosPage, setProductosPage] = useState(0); // 0-based
  const [productosRowsPerPage] = useState(10);
  const [productosTotalCount, setProductosTotalCount] = useState(0);

  const handleFormularioChange = (updates: Partial<RemitoCompraFormularioForm>) => {
    setFormulario((f) => ({ ...f, ...updates }));
  };

  const handleItemChange = (idx: number, updates: Partial<RemitoCompraItemForm>) => {
    setItems((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...updates };
      return next;
    });
  };

  const removeItem = (idx: number) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const addItem = () => setItems((prev) => [...prev, { ...defaultItem }]);

  const handleProveedorSelect = (prov: any) => {
    setFormulario((f) => ({
      ...f,
      nroProveedor: prov.nroProveedor,
      nombre: prov.nombre,
      cuit: prov.cuit,
    }));
    setProveedorNombre(prov.nombre || '');
    setOpenProveedorSearch(false);
  };

  const handleUbicacionSelect = (u: any) => {
    setFormulario((f) => ({
      ...f,
      nroUbicacionOrigen: u.nroUbicacion,
    }));
    setUbicacionNombre(u.nombreUbicacion || '');
    setOpenUbicacionSearch(false);
  };

  const loadProductos = useCallback(async () => {
    if (!openProductSearch) return;
    try {
      const res = await productoService.getProductos({
        searchTerm: productSearchTerm || undefined,
        pageNumber: productosPage + 1,
        pageSize: productosRowsPerPage,
      });
      setProductos(res.items || []);
      setProductosTotalCount(res.totalCount || 0);
    } catch {
      setProductos([]);
      setProductosTotalCount(0);
    }
  }, [openProductSearch, productSearchTerm, productosPage, productosRowsPerPage]);

  useEffect(() => {
    if (openProductSearch) loadProductos();
  }, [openProductSearch, loadProductos]);

  const handleSelectProduct = (p: any) => {
    const idx = productSearchIndex;
    if (idx === null) return;
    const codigo = p.codigoArticulo || p.codigo || '';
    const desc = p.descripcion || p.descripcionArticulo || '';
    const costo = Number(p.costo) || 0;
    setItems((prev) => {
      const next = [...prev];
      next[idx] = {
        ...next[idx],
        codigoArticulo: String(codigo),
        descripcion: String(desc),
        precioUnitario: costo,
      };
      return next;
    });
    setOpenProductSearch(false);
    setProductSearchIndex(null);
  };

  const handleCodigoBlurOrEnter = useCallback(
    async (idx: number, codigoValue: string) => {
      const codigo = (codigoValue || '').trim();
      if (!codigo) return;
      try {
        const p = await productoService.getProducto(codigo);
        const desc = (p as any).descripcion || (p as any).descripcionArticulo || '';
        const costo = Number((p as any).costo) || Number((p as any).costoBrutoUnitario) || 0;
        handleItemChange(idx, { codigoArticulo: codigo, descripcion: desc, precioUnitario: costo });
      } catch {
        enqueueSnackbar(`No se encontró el producto con código "${codigo}"`, { variant: 'warning' });
      }
    },
    [enqueueSnackbar]
  );

  const handleSubmit = async () => {
    if (!formulario.nroProveedor || formulario.nroProveedor <= 0) {
      enqueueSnackbar('Debe seleccionar un proveedor', { variant: 'warning' });
      return;
    }
    if (!formulario.nroSuc || formulario.nroSuc <= 0) {
      enqueueSnackbar('Debe ingresar una sucursal válida', { variant: 'warning' });
      return;
    }
    if (!formulario.nroUbicacionOrigen || formulario.nroUbicacionOrigen <= 0) {
      enqueueSnackbar('Debe seleccionar una ubicación origen válida', { variant: 'warning' });
      return;
    }

    const itemsValidos = items.filter(
      (it) =>
        (it.codigoArticulo || '').trim().length > 0 && Number(it.cantidad) > 0 && Number(it.precioUnitario) > 0
    );

    if (itemsValidos.length === 0) {
      enqueueSnackbar('Debe agregar al menos un ítem válido (código, cantidad y precio > 0)', { variant: 'warning' });
      return;
    }

    const payload = {
      formulario: {
        nroSuc: Number(formulario.nroSuc),
        nroProveedor: Number(formulario.nroProveedor),
        nombre: formulario.nombre || undefined,
        cuit: formulario.cuit || undefined,
        descripcion: formulario.descripcion?.trim() || undefined,
        fechaEmision: formulario.fechaEmision || todayIsoDate(),
        fechaVencim: formulario.fechaVencim || undefined,
        nroLista: formulario.nroLista?.trim() || undefined,
        nroUbicacionOrigen: formulario.nroUbicacionOrigen,
        direccionEnvio: formulario.direccionEnvio?.trim() || undefined,
        codigoCiudadEnvio: formulario.codigoCiudadEnvio,
        formaEntrega: formulario.formaEntrega?.trim() || undefined,
        porcDesc: formulario.porcDesc,
        porcDesc2: formulario.porcDesc2,
        porcDesc3: formulario.porcDesc3,
      },
      items: itemsValidos.map((it) => ({
        codigoArticulo: it.codigoArticulo.trim(),
        descripcion: it.descripcion?.trim() || undefined,
        cantidad: Number(it.cantidad),
        precioUnitario: Number(it.precioUnitario),
        porcDesc: Number(it.porcDesc ?? 0),
        notas: it.notas?.trim() || undefined,
      })),
    };

    try {
      setSaving(true);
      const created = await remitoCompraService.createRemitoCompra(payload as any);
      enqueueSnackbar('Remito de compra creado correctamente', { variant: 'success' });
      navigate(`/compras/remitos/${created.nroFormulario}`);
    } catch (error: unknown) {
      const msg = getApiErrorMessage(error, 'Error al crear el remito de compra');
      enqueueSnackbar(msg, { variant: 'error', autoHideDuration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#1976d2' }}>
            Nuevo Remito de Compra
          </Typography>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
            Encabezado
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Proveedor"
                value={proveedorNombre || (formulario.nroProveedor ? `${formulario.nroProveedor}` : '')}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setOpenProveedorSearch(true)} edge="end" size="small">
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                onClick={() => setOpenProveedorSearch(true)}
                required
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Sucursal"
                type="number"
                value={String(formulario.nroSuc ?? 0)}
                onChange={(e) => handleFormularioChange({ nroSuc: parseInt(e.target.value, 10) || 0 })}
                inputProps={{ inputMode: 'numeric' }}
                required
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Fecha emisión"
                type="date"
                value={formulario.fechaEmision}
                onChange={(e) => handleFormularioChange({ fechaEmision: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Fecha vencimiento"
                type="date"
                value={formulario.fechaVencim || ''}
                onChange={(e) => handleFormularioChange({ fechaVencim: e.target.value || undefined })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Nro. Lista"
                value={formulario.nroLista || ''}
                onChange={(e) => handleFormularioChange({ nroLista: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ubicación origen"
                value={ubicacionNombre || (formulario.nroUbicacionOrigen ? `${formulario.nroUbicacionOrigen}` : '')}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setOpenUbicacionSearch(true)} edge="end" size="small">
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                onClick={() => setOpenUbicacionSearch(true)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Descripción"
                value={formulario.descripcion || ''}
                onChange={(e) => handleFormularioChange({ descripcion: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                type="number"
                label="% Desc"
                value={formulario.porcDesc ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  handleFormularioChange({ porcDesc: v === '' ? undefined : parseFloat(v) });
                }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                type="number"
                label="% Desc 2"
                value={formulario.porcDesc2 ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  handleFormularioChange({ porcDesc2: v === '' ? undefined : parseFloat(v) });
                }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                type="number"
                label="% Desc 3"
                value={formulario.porcDesc3 ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  handleFormularioChange({ porcDesc3: v === '' ? undefined : parseFloat(v) });
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Dirección de envío"
                value={formulario.direccionEnvio || ''}
                onChange={(e) => handleFormularioChange({ direccionEnvio: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Codigo ciudad envío"
                value={formulario.codigoCiudadEnvio ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  handleFormularioChange({ codigoCiudadEnvio: v === '' ? undefined : parseInt(v, 10) || undefined });
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Forma entrega"
                value={formulario.formaEntrega || ''}
                onChange={(e) => handleFormularioChange({ formaEntrega: e.target.value })}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
            Detalle
          </Typography>

          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Cantidad
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Precio unit.
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    % Desc
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Notas</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">
                    Acción
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((it, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell sx={{ width: 170 }}>
                      <TextField
                        fullWidth
                        size="small"
                        value={it.codigoArticulo}
                        placeholder="Código"
                        onChange={(e) => handleItemChange(idx, { codigoArticulo: e.target.value })}
                        onBlur={(e) => handleCodigoBlurOrEnter(idx, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleCodigoBlurOrEnter(idx, (e.target as HTMLInputElement).value);
                          }
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setProductSearchIndex(idx);
                                  setOpenProductSearch(true);
                                }}
                              >
                                <SearchIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>

                    <TableCell sx={{ minWidth: 260 }}>
                      <TextField
                        fullWidth
                        size="small"
                        value={it.descripcion}
                        placeholder="Descripción"
                        onChange={(e) => handleItemChange(idx, { descripcion: e.target.value })}
                      />
                    </TableCell>

                    <TableCell sx={{ width: 140 }}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        value={Number.isFinite(it.cantidad) ? it.cantidad : 0}
                        inputProps={{ min: 0, step: 0.01 }}
                        onChange={(e) => handleItemChange(idx, { cantidad: parseFloat(e.target.value) || 0 })}
                      />
                    </TableCell>

                    <TableCell sx={{ width: 160 }}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        value={Number.isFinite(it.precioUnitario) ? it.precioUnitario : 0}
                        inputProps={{ min: 0, step: 0.01 }}
                        onChange={(e) => handleItemChange(idx, { precioUnitario: parseFloat(e.target.value) || 0 })}
                      />
                    </TableCell>

                    <TableCell sx={{ width: 130 }}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        value={Number.isFinite(it.porcDesc) ? it.porcDesc : 0}
                        inputProps={{ min: 0, max: 100, step: 0.01 }}
                        onChange={(e) => handleItemChange(idx, { porcDesc: parseFloat(e.target.value) || 0 })}
                      />
                    </TableCell>

                    <TableCell sx={{ minWidth: 220 }}>
                      <TextField
                        fullWidth
                        size="small"
                        value={it.notas}
                        placeholder="Notas"
                        onChange={(e) => handleItemChange(idx, { notas: e.target.value })}
                      />
                    </TableCell>

                    <TableCell sx={{ width: 90 }} align="center">
                      <IconButton
                        size="small"
                        onClick={() => removeItem(idx)}
                        disabled={items.length <= 1}
                        sx={{ color: items.length <= 1 ? 'rgba(0,0,0,0.26)' : 'error.main' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack direction="row" spacing={1} sx={{ mb: 3, justifyContent: 'flex-start' }}>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={addItem}>
              Agregar item
            </Button>
          </Stack>

          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button variant="outlined" onClick={() => navigate('/compras/remitos')} disabled={saving}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleSubmit} disabled={saving} sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}>
              {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Guardar'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <ProveedorSearch open={openProveedorSearch} onClose={() => setOpenProveedorSearch(false)} onSelect={handleProveedorSelect} />
      <UbicacionSearch open={openUbicacionSearch} onClose={() => setOpenUbicacionSearch(false)} onSelect={handleUbicacionSelect} />

      <Dialog
        open={openProductSearch}
        onClose={() => {
          setOpenProductSearch(false);
          setProductSearchIndex(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Buscar artículo</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1} sx={{ mb: 2, mt: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por código o descripción..."
              value={productSearchTerm}
              onChange={(e) => setProductSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setProductosPage(0);
                  loadProductos();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              onClick={() => {
                setProductosPage(0);
                loadProductos();
              }}
              sx={{ color: '#1976d2', borderColor: '#1976d2' }}
            >
              Buscar
            </Button>
          </Stack>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell align="right">Costo</TableCell>
                  <TableCell align="center">Seleccionar</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productos.map((p) => (
                  <TableRow key={p.codigoArticulo || p.codigo || ''} hover>
                    <TableCell>{p.codigoArticulo || p.codigo || '-'}</TableCell>
                    <TableCell>{p.descripcion || p.descripcionArticulo || '-'}</TableCell>
                    <TableCell align="right">{formatCurrency(Number(p.costo) || 0)}</TableCell>
                    <TableCell align="center">
                      <Button size="small" onClick={() => handleSelectProduct(p)}>
                        Seleccionar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={productosTotalCount}
            page={productosPage}
            onPageChange={(_, p) => setProductosPage(p)}
            rowsPerPage={productosRowsPerPage}
            onRowsPerPageChange={() => {}}
            rowsPerPageOptions={[10]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenProductSearch(false);
              setProductSearchIndex(null);
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RemitoCompraForm;

